import { AppError, toAppError } from './app-error';
import { apiClient } from './client';
import { getApiConfig } from './configure';

import type { InternalAxiosRequestConfig } from 'axios';

/**
 * Axios runs response handlers in registration order and request handlers in reverse, so
 * this file is the only place interceptors are registered. Refresh-and-replay registers
 * first (it needs the raw AxiosError's `.config`/`.response`); normalization registers last,
 * or the refresh handler would receive an already-normalized AppError and silently stop working.
 */

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/** Marks the one `auth` rejection that already ended the session, so the app does not report it twice. */
export const REFRESH_FAILED_CODE = 'client.refresh_failed';

let installed = false;

export function installInterceptors(): void {
  // Re-installing would double every handler: two refreshes per 401, double-wrapped errors.
  if (installed) return;
  installed = true;

  apiClient.interceptors.request.use((request) => {
    const { baseURL, getAccessToken } = getApiConfig();
    // Resolved per-request, not baked into axios.create: boot config may happen after
    // this module is imported.
    request.baseURL ??= baseURL;
    const token = getAccessToken();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  });

  // 1. Refresh-and-replay, registered first so it sees the raw AxiosError. Refresh itself
  //    (single-flight, the bare refresh client, multi-tab coordination) is injected: it's
  //    the app's concern. This package only guarantees where in the chain it runs.
  apiClient.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      const config = (error as { config?: RetryableConfig }).config;
      const status = (error as { response?: { status?: number } }).response?.status;

      if (status !== 401 || !config || config._retry) throw error;

      // Marks the request so a replay that 401s again surfaces the error
      // instead of looping forever.
      config._retry = true;

      const { refresh, onAuthFailure } = getApiConfig();
      let token: string;
      try {
        token = await refresh();
      } catch (refreshError) {
        // Only a rejected refresh is terminal: the app clears the session and the next boot
        // short-circuits to anonymous. Thrown as `auth` so the query retry policy stops here
        // instead of replaying with no bearer and re-arming this handler.
        onAuthFailure(refreshError);
        throw new AppError({
          kind: 'auth',
          code: REFRESH_FAILED_CODE,
          message: 'Session expired',
          cause: refreshError,
        });
      }

      // Deliberately outside the try: the replay's own failure (a 404, a 500, or a cancel
      // because the caller unmounted mid-refresh) is an ordinary request error. Treating it as
      // terminal tears down a session that was just renewed and cancels every sibling replay.
      config.headers.set('Authorization', `Bearer ${token}`);
      return apiClient.request(config);
    },
  );

  // 2. Normalization. Registered LAST so every rejection leaving this client is
  //    an AppError, and nothing downstream ever sees an AxiosError.
  apiClient.interceptors.response.use(
    (response) => response,
    (error: unknown) => Promise.reject(toAppError(error)),
  );
}

/** Test helper — allows a suite to reinstall a clean chain. */
export function resetInterceptors(): void {
  apiClient.interceptors.request.clear();
  apiClient.interceptors.response.clear();
  installed = false;
}
